import { useContext, useEffect, useMemo, useCallback, useState } from 'react';
import TeamsContext from '@providers/Teams/TeamsContext';
import useNoodleApi from '@hooks/useNoodleApi';
import * as tsClient from '@tsClient';
import { SectionTemplate } from '@/typings/questionnaires-api-models';
import FormEditor from '@/components/Questionnaire/questionnaireEditor/FormEditor';
import { Questionnaire } from '@/typings/Questionnaire';
import { useToast } from '@/hooks';
import ProgressBarLoader from '@/components/ProgressBarLoader';

type Props = { slug: string };

const QuestionnaireEditor: React.FC<Props> = ({ slug }) => {
  const { currentTeamOwner } = useContext(TeamsContext);
  const {
    data: questionnaireTemplate,
    fetchingState: { isFetching },
    getData: getQuestionnaireTemplate,
  } = useNoodleApi(tsClient.questionnaires.getQuestionnaireTemplateBySlug);
  const { getData: publishQuestionnaireTemplate } = useNoodleApi(tsClient.questionnaires.publishQuestionnaireTemplate, { toastOnError: true });
  const { getData: getFieldTemplates } = useNoodleApi(tsClient.questionnaires.getFieldTemplatesForSectionTemplate);
  const [isSaving, setIsSaving] = useState(false);
  const [sectionFields, setSectionFields] = useState<Record<string, Array<unknown>>>({});
  const [template, setTemplate] = useState<Awaited<ReturnType<typeof tsClient.questionnaires.getQuestionnaireTemplateBySlug>>>();
  const [formData, setFormData] = useState<Questionnaire.Questionnaire>();
  const toast = useToast();

  const creatorId = currentTeamOwner?.id;

  const fetchQuestionnaireTemplate = useCallback(() => {
    if (creatorId) {
      getQuestionnaireTemplate({
        creatorId,
        questionnaireTemplateSlug: slug,
        statusOrVersion: 'draft',
        usesCasebuilderQuestionnaire: false,
      });
    }
  }, [getQuestionnaireTemplate, creatorId, slug]);

  useEffect(() => {
    if (questionnaireTemplate) {
      setTemplate(questionnaireTemplate);
    }
  }, [questionnaireTemplate]);

  useEffect(() => {
    fetchQuestionnaireTemplate();
  }, [fetchQuestionnaireTemplate]);

  const getFieldTemplatesForSectionTemplate = useCallback(async (section: Pick<SectionTemplate, 'id' | 'slug' | 'questionnaireTemplateId'>): Promise<Array<Questionnaire.Field>> => {
    const sectionTemplateId = section.id;
    if (creatorId && sectionTemplateId) {
      const { data: fields } = await getFieldTemplates({
        creatorId,
        questionnaireTemplateId: section.questionnaireTemplateId,
        sectionTemplateId,
      });
      if (fields) {
        const newFields = fields.map((field) => ({
          ...field,
          assignedTo: field.fieldAssigneeTemplates?.map((assignee) => assignee.questionnaireAssigneeTemplateId) ?? [],
          description: field.descriptionText ?? '',
          id: field.id,
          validation: field.validation || undefined,
        }));
        setSectionFields(prev => ({
          ...prev,
          [sectionTemplateId]: newFields,
        }));
        return newFields as unknown as Array<Questionnaire.Field>;
      }
    }
    return [];
  }, [creatorId, getFieldTemplates]);

  const handlePublish = useCallback(async ({ versionNote }: { versionNote: string }) => {
    if (!creatorId || !template) return;
    setIsSaving(true);
    const data = await publishQuestionnaireTemplate({
      creatorId,
      questionnaireTemplateId: template.id,
      versionNote,
    });
    if (data.data) {
      setTemplate(data.data);
      toast(useToast.ToastTypeVariants.SUCCESS, 'Questionnaire published successfully!');
    }
    setIsSaving(false);
  }, [toast, template, creatorId, publishQuestionnaireTemplate]);

  const updateSectionFields = useCallback(({ sectionId, newFields }: { sectionId: string; newFields: Array<Questionnaire.FieldCommon> }): void => {
    setSectionFields(prev => ({
      ...prev,
      [sectionId]: newFields,
    }));
  }, []);

  useEffect(() => {
    if (template && creatorId) {
      setFormData({
        ...template,
        assigneeTemplates: template.questionnaireAssigneeTemplates,
        creatorId,
        description: template.descriptionText ?? '',
        id: template.id!,
        questionnaireTemplateCreatorId: template.creatorId,
        questionnaireTemplateId: template.id,
        sections: (template.sectionTemplates ?? [])
          .map((section) => ({
            ...section,
            description: section.descriptionText ?? '',
            // @todo: support html text later
            fields: sectionFields[section.id] as Questionnaire.Field[] ?? [],
            id: section.id,
            isLoading: !sectionFields[section.id],
          }))
          .sort((a, b) => {
            const rankA = a.rank ?? Number.MAX_SAFE_INTEGER;
            const rankB = b.rank ?? Number.MAX_SAFE_INTEGER;
            return rankA - rankB;
          }),
        updatedAt: new Date(template.updatedAt),
        updatedBy: template.updatedBy ?? undefined,
        updatedByName: template.updatedByName ?? undefined,
        versionNote: template.versionNote ?? undefined,
      });
    }
  }, [template, sectionFields]);

  const fieldsByKey: Record<string, Questionnaire.Field> = useMemo(() => {
    if (!formData) return {};

    return formData.sections.reduce((acc: Record<string, Questionnaire.Field>, section) => {
      section.fields.forEach((field) => {
        if (!acc[field.key]) {
          acc[field.key] = field;
        }
      });
      return acc;
    }, {});
  }, [formData]);

  if (isFetching) {
    return <ProgressBarLoader
      chunks={[
        {
          label: "Fetching questionnaire template...",
          loaded: false,
        },
      ]}
    />;
  }

  if (!creatorId || !template) {
    return null;
  }

  return formData
    ? (
      <FormEditor
        fieldsByKey={fieldsByKey}
        creatorId={creatorId}
        formData={formData}
        isSaving={isSaving}
        onPublish={handlePublish}
        getFieldTemplatesForSectionTemplate={getFieldTemplatesForSectionTemplate}
        updateSectionFields={updateSectionFields}
        updateTemplate={setFormData}
      />
    )
    : null;
};

export default QuestionnaireEditor;
